<script setup lang="ts">
import {computed, ref} from 'vue';
import {Icon} from '@iconify/vue';
import {BASS_PREVIEW_PART_NAME} from '@/composables/musicXmlDom';
import {injectScorePlayback} from './playbackContext';

// Player-State + Aktionen aus dem `ScorePlaybackContext`. Strikter Inject (kein null-Fallback):
// `PlaybackControlsOverlay` rendert diese Komponente nur, wenn der Provider existiert – also nur auf der
// Eingabe-Vorschau. Auf der Griffschrift-Ausgabe gibt es gar keinen Player (siehe
// `playbackContext.ts`), deshalb braucht es hier auch kein `isInputTabOpen`-Gate mehr.
const {
  isPlaying,
  isPlayerReady,
  isLoadingSounds,
  soundLoadLabel,
  currentBpm,
  metronomeEnabled,
  tracks,
  play,
  pause,
  stop,
  setBpm,
  setMetronome,
  setTrackVolume,
  setTrackMuted,
  setTrackSolo,

} = injectScorePlayback();

const showMixer = ref(false);

function onBpmInput(event: Event) {
  setBpm(Number((event.target as HTMLInputElement).value));
}

function onVolumeInput(id: number, event: Event) {
  setTrackVolume(id, Number((event.target as HTMLInputElement).value));
}

// Abkürzung zur wichtigsten Spur: das erzeugte Bass-System soll man beim Prüfen der Akkorde gegen
// die Melodie abwägen können, ohne erst das Mischpult aufzuklappen. Ohne Bass-System kein Regler.
const bassTrack = computed(() => tracks.value.find((track) => track.name === BASS_PREVIEW_PART_NAME) ?? null);
</script>

<template>
  <div class="flex flex-none items-center gap-2 rounded-lg  bg-gray-50 p-1">
  <!-- Transport -->
  <!-- Play ist während des (einmaligen) SoundFont-Ladens gesperrt und zeigt einen Spinner; ohne
       geladene Fonts würde der Player die ersten Noten still verschlucken. -->
  <button
      type="button"
      class="flex h-7 w-7 text-green-500  bg-white  items-center justify-center rounded text-gray-600 transition-colors hover:bg-white disabled:opacity-30"
      :disabled="!isPlayerReady || (!isPlaying && isLoadingSounds)"
      :title="!isPlaying && isLoadingSounds ? soundLoadLabel : (isPlaying ? 'Pause' : 'Wiedergabe')"
      @click="isPlaying ? pause() : play()"
  >
    <Icon
        v-if="!isPlaying && isLoadingSounds"
        icon="fa6-solid:spinner"
        class="animate-spin text-xs text-blue-500"
    />
    <Icon v-else :icon="isPlaying ? 'fa6-solid:pause' : 'fa6-solid:play'" class="text-xs"/>
  </button>
  <button
      type="button"
      class="flex h-7 w-7  text-red-500  bg-gray-100  items-center justify-center rounded  transition-colors hover:bg-white disabled:opacity-30"

      title="Stopp"
      @click="stop()"
  >
    <Icon icon="fa6-solid:stop" class="text-xs"/>
  </button>

  <div class="mx-0.5 h-4 w-px bg-gray-300"></div>

  <!-- Tempo -->
  <div class="flex items-center gap-1.5 px-1" title="Tempo (BPM)">
    <Icon icon="fa6-solid:gauge-high" class="text-xs text-gray-500"/>
    <input
        type="range"
        min="30"
        max="240"
        step="1"
        :value="currentBpm"
        :disabled="!isPlayerReady"
        class="h-1.5 w-16 cursor-pointer appearance-none rounded-lg bg-gray-300 accent-blue-600 disabled:opacity-30"
        @input="onBpmInput"
    />
    <span class="w-12 text-right font-mono text-xs text-gray-600">{{ currentBpm }}</span>
  </div>

  <div class="mx-0.5 h-4 w-px bg-gray-300"></div>

  <!-- Bass-Lautstärke (nur bei vorhandenem Bass-Vorschau-System) -->
  <template v-if="bassTrack">
    <div class="flex items-center gap-1.5 px-1" title="Lautstärke des Bass-Systems">
      <Icon icon="game-icons:f-clef" class="text-sm text-violet-500"/>
      <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          :value="bassTrack.volume"
          :disabled="!isPlayerReady || bassTrack.muted"
          class="h-1.5 w-16 cursor-pointer appearance-none rounded-lg bg-gray-300 accent-violet-600 disabled:opacity-30"
          @input="onVolumeInput(bassTrack.id, $event)"
      />
      <span class="w-9 text-right font-mono text-xs text-gray-600">{{ Math.round(bassTrack.volume * 100) }}%</span>
    </div>

    <div class="mx-0.5 h-4 w-px bg-gray-300"></div>
  </template>

  <!-- Metronome -->
  <button
      type="button"
      class="flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-white disabled:opacity-30"
      :class="metronomeEnabled ? 'bg-white text-blue-600' : 'text-gray-600'"
      :disabled="!isPlayerReady"
      :title="metronomeEnabled ? 'Metronom aus' : 'Metronom an'"
      @click="setMetronome(!metronomeEnabled)"
  >
    <Icon icon="fa6-solid:bell" class="text-xs"/>
  </button>

  <!-- Mixer -->
  <div class="relative">
    <button
        type="button"
        class="flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-white disabled:opacity-30"
        :class="showMixer ? 'bg-white text-blue-600' : 'text-gray-600'"
        :disabled="!isPlayerReady || tracks.length === 0"
        title="Mixer"
        @click="showMixer = !showMixer"
    >
      <Icon icon="fa6-solid:sliders" class="text-xs"/>
    </button>

    <!-- Mixer popover -->
    <div
        v-if="showMixer"
        class="absolute right-0 top-9 z-20 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-xl ring-1 ring-black/5"
    >
      <div class="mb-2 flex items-center justify-between">
        <span class="text-xs font-semibold text-gray-700">Mixer</span>
        <button
            type="button"
            class="text-gray-400 transition-colors hover:text-gray-600"
            title="Schließen"
            @click="showMixer = false"
        >
          <Icon icon="fa6-solid:xmark" class="text-xs"/>
        </button>
      </div>
      <div class="flex max-h-64 flex-col gap-3 overflow-auto">
        <div v-for="track in tracks" :key="track.id" class="flex flex-col gap-1">
          <div class="flex items-center justify-between gap-2">
            <span class="truncate text-xs font-medium text-gray-700" :title="track.name">{{ track.name }}</span>
            <div class="flex items-center gap-1">
              <button
                  type="button"
                  class="flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold transition-colors"
                  :class="track.muted ? 'bg-red-100 text-red-600' : 'text-gray-400 hover:bg-gray-100'"
                  title="Stumm"
                  @click="setTrackMuted(track.id, !track.muted)"
              >
                M
              </button>
              <button
                  type="button"
                  class="flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold transition-colors"
                  :class="track.solo ? 'bg-amber-100 text-amber-600' : 'text-gray-400 hover:bg-gray-100'"
                  title="Solo"
                  @click="setTrackSolo(track.id, !track.solo)"
              >
                S
              </button>
            </div>
          </div>
          <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              :value="track.volume"
              :disabled="track.muted"
              class="h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-gray-300 accent-blue-600 disabled:opacity-30"
              @input="onVolumeInput(track.id, $event)"
          />
        </div>
      </div>
    </div>
  </div>
  </div>
</template>
